import React, { useRef, useState, useEffect } from 'react';
import { Eraser, Check, Pencil, Trash2 } from 'lucide-react';

export default function Drawing({ question, status, onChoiceClick }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const [ctx, setCtx] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isErasing, setIsErasing] = useState(false);

  // Холст растягивается на доступную высоту контейнера (и на всю ширину),
  // а панель кнопок остаётся в потоке снизу — «Готово» всегда видно без скролла.
  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      // Минимум 200px, максимум 520px — чтобы на планшете не гигантский.
      const side = Math.max(200, Math.min(520, Math.floor(Math.min(rect.width, window.innerHeight * 0.5))));
      canvas.width = side;
      canvas.height = side;
      const context = canvas.getContext('2d');
      context.lineCap = 'round';
      context.lineJoin = 'round';
      context.strokeStyle = '#1f2937';
      setCtx(context);
    };

    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  const getCoords = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const setupContext = () => {
    if (!ctx) return;
    if (isErasing) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineWidth = 20;
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.lineWidth = 3;
      ctx.strokeStyle = '#1f2937';
    }
  };

  const startDrawing = (e) => { 
    e.preventDefault(); 
    setIsDrawing(true); 
    setupContext(); 
    const { x, y } = getCoords(e); 
    ctx.beginPath(); 
    ctx.moveTo(x, y); 
  };
  
  const draw = (e) => { 
    if (!isDrawing || !ctx) return; 
    e.preventDefault(); 
    const { x, y } = getCoords(e); 
    ctx.lineTo(x, y); 
    ctx.stroke(); 
  };
  
  const finishDrawing = () => { 
    if (!ctx) return; 
    setIsDrawing(false); 
    ctx.beginPath(); 
  };
  
  const clearCanvas = () => { 
    if (ctx) ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height); 
  };

  const handleDone = () => {
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onChoiceClick(dataUrl);
  };

  return (
    <div className="flex flex-col items-center gap-3 w-full max-w-4xl h-full">
      <h2 className="text-xl md:text-3xl font-semibold text-primary text-center">{question.ask}</h2>
      <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 w-full flex-1 min-h-0">
        {question.imageUrl && (
          <div className="flex flex-col items-center gap-1">
            <span className="text-xs md:text-sm text-muted-foreground">Образец:</span>
            <img src={question.imageUrl} alt="Образец" className="max-h-[120px] md:max-h-[200px] w-auto rounded-2xl border-2 border-border bg-white p-2 object-contain" />
          </div>
        )}
        <div ref={wrapRef} className="flex flex-col items-center gap-1 w-full md:w-auto flex-1 min-h-0">
          <span className="text-xs md:text-sm text-muted-foreground">Поле пациента:</span>
          <div className="bg-white rounded-2xl shadow-lg border-2 border-border p-1 max-w-full">
            <canvas 
              ref={canvasRef} 
              onMouseDown={startDrawing} 
              onMouseUp={finishDrawing} 
              onMouseLeave={finishDrawing} 
              onMouseMove={draw} 
              onTouchStart={startDrawing} 
              onTouchEnd={finishDrawing} 
              onTouchMove={draw} 
              className="touch-none cursor-crosshair rounded-xl"
            />
          </div>
        </div>
      </div>
      
      {/* Панель инструментов — всегда видима: компактные кнопки в один ряд */}
      <div className="flex flex-wrap gap-2 justify-center w-full py-2">
        <button 
          onClick={() => setIsErasing(false)} 
          className={`flex items-center gap-2 px-3 py-2 rounded-xl font-semibold text-sm ${!isErasing ? 'bg-primary text-white' : 'bg-secondary text-primary border border-border'}`}
        >
          <Pencil className="size-4" /> Карандаш
        </button>
        <button 
          onClick={() => setIsErasing(true)} 
          className={`flex items-center gap-2 px-3 py-2 rounded-xl font-semibold text-sm ${isErasing ? 'bg-primary text-white' : 'bg-secondary text-primary border border-border'}`}
        >
          <Eraser className="size-4" /> Ластик
        </button>
        <button 
          onClick={clearCanvas} 
          className="flex items-center gap-2 px-3 py-2 bg-secondary text-primary rounded-xl font-semibold text-sm border border-border hover:bg-border"
        >
          <Trash2 className="size-4" /> Очистить всё
        </button>
        <button 
          onClick={handleDone} 
          className="flex items-center gap-2 px-6 py-2 bg-green-500/20 text-green-600 border-2 border-green-500 rounded-xl font-semibold text-sm hover:bg-green-500/30"
        >
          <Check className="size-4" /> Готово
        </button>
      </div>
    </div>
  );
}